import React from 'react';
import { cn } from 'cn';
import { Tabs as UITabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import Badge from 'src/componentes/Badge';

type TabElement = React.ReactElement<{
  label?: React.ReactNode;
  badgeCount?: number;
  navItemClass?: string;
  value?: string;
  active?: boolean;
}>;

type TabsProps = {
  /** As abas: cada filho é um `Tab`. Filhos `null`/`false` são ignorados (aba condicional). */
  children?: TabElement | null | false | (TabElement | null | false)[];
  /** Índice da aba aberta. Ao mudar, a aba muda. Sem ele, começa na primeira. */
  currenttabIndex?: number;
  /** Chamado com o índice ao clicar numa aba, inclusive na que já está aberta. */
  onSelect?: (index: number) => void;
  /** Índices das abas com erro de validação: o título fica vermelho. */
  tabsError?: number[];
  /** Padding da área de conteúdo (ex.: `0` em telas que já têm o próprio espaço). */
  paddingContent?: number | string;
  /** Estilo do contêiner das abas. */
  style?: React.CSSProperties;
  /** Estilo da área de conteúdo. */
  styleContent?: React.CSSProperties;
  /** Classes do cabeçalho. `d-none` esconde o cabeçalho e deixa só o conteúdo. */
  tabClass?: string;
  /** Quando muda (sem `currenttabIndex`), volta para a primeira aba. */
  tabIndex?: number;
  /** Reaplica o `currenttabIndex` mesmo que ele não tenha mudado. */
  forceChangeTab?: boolean;
};

type TabsState = { currenttabIndex: number };

/**
 * Abas com cabeçalho no estilo do portal. Cada filho é um `Tab` com `label`.
 * @import import Tabs from 'src/componentes/tabs/tabs';
 */
class Tabs extends React.Component<TabsProps, TabsState> {
  constructor(props: TabsProps) {
    super(props);
    this.state = {
      currenttabIndex: this.props.currenttabIndex
        ? this.props.currenttabIndex
        : 0,
    };
  }

  UNSAFE_componentWillReceiveProps(newProps: TabsProps) {
    if (newProps.tabIndex != this.props.tabIndex && !newProps.currenttabIndex) {
      this.setState({ currenttabIndex: 0 });
    } else if (
      (newProps.currenttabIndex || newProps.currenttabIndex == 0) &&
      newProps.currenttabIndex != this.props.currenttabIndex
    ) {
      this.setState({ currenttabIndex: newProps.currenttabIndex });
    } else if (newProps.forceChangeTab) {
      this.setState({ currenttabIndex: newProps.currenttabIndex as number });
    }
  }

  gototabIndex = (index: number) => {
    this.setState({ currenttabIndex: index });

    this.props.onSelect && this.props.onSelect(index);
  };

  render() {
    const {
      children,
      tabsError = [],
      paddingContent,
      style,
      styleContent,
      tabClass,
    } = this.props;
    const tabs = (children instanceof Array ? children : [children]).filter(
      (t): t is TabElement => !!t
    );
    const { currenttabIndex } = this.state;

    const escondeCabecalho =
      String(tabClass || '').includes('d-none') ||
      (tabs.length > 0 &&
        tabs.every((tab) =>
          String(tab.props?.navItemClass || '').includes('d-none')
        ));

    return (
      <UITabs
        value={String(currenttabIndex)}
        onValueChange={(value) => this.gototabIndex(Number(value))}
        activationMode="manual"
        className={cn('tab tw:block', {
          'tab--sem-cabecalho': escondeCabecalho,
        })}
        style={style}
      >
        <TabsList className={cn('nav-tabs', tabClass)}>
          {tabs.map((tab, tabIndex) => (
            <TabsTrigger
              key={tabIndex}
              value={String(tabIndex)}
              // O Radix não chama onValueChange na aba já ativa.
              onMouseDown={(e) => {
                if (
                  e.button === 0 &&
                  !e.ctrlKey &&
                  currenttabIndex == tabIndex
                ) {
                  this.gototabIndex(tabIndex);
                }
              }}
              onKeyDown={(e) => {
                if (
                  (e.key === 'Enter' || e.key === ' ') &&
                  currenttabIndex == tabIndex
                ) {
                  this.gototabIndex(tabIndex);
                }
              }}
              className={cn(
                tab.props.navItemClass,
                tabsError?.includes(tabIndex) &&
                  'tw:text-destructive tw:hover:text-destructive tw:data-[state=active]:text-destructive'
              )}
            >
              {tab.props.label}
              {(tab.props.badgeCount ?? 0) > 0 && (
                <Badge className="tw:min-w-7 tw:text-xs" color="primaryLight">
                  {tab.props.badgeCount}
                </Badge>
              )}
            </TabsTrigger>
          ))}
        </TabsList>
        <div
          className="tab-content"
          style={{ padding: paddingContent, ...styleContent }}
        >
          {tabs.map((tab, tabIndex) =>
            React.cloneElement(tab, {
              key: tab.key || tabIndex,
              value: String(tabIndex),
              active: currenttabIndex == tabIndex,
            })
          )}
        </div>
      </UITabs>
    );
  }
}

export default Tabs;
